import React, { ReactElement, useState } from 'react';
import {
    GridColDef,
    GridOverlay,
    GridOverlayProps,
    GridToolbarContainer,
    useGridApiContext,
} from '@mui/x-data-grid-pro';
import * as MaterialUIIcons from '../../../../app/common/controls/MaterialUIIcons';
import { Box, Divider, IconButton, ListItemText, MenuItem, Tooltip, Typography, } from '@mui/material';
import AdminDataGridFilter from './AdminDataGridFilter';
import { useStyles } from './AdminDataGridStyles';
import { Refresh, ViewWeek } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Filter, FilterGroup } from '../../../utils/filter';
import { downloadFile } from '../../../utils/fileUtils';
import { EksportSettings } from '../../../../models/eksportSettings';
import { Column } from '../../../../models';
import { GridSlotsComponentsProps } from '@mui/x-data-grid';
import AdminDataGridColumnsMenu from './AdminDataGridColumnsMenu';
import { AcosIcon } from '../../controls/AcosIcon';
import EditPanelContextMenu from '../EditPanelDataGrid/EditPanelCtxMenu';
import { FullscreenSpinner } from '../../controls/FullscreenSpinner/FullscreenSpinner';
import { MessageDialog } from '../MessageDialog/MessageDialog';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useDataGridRoutes } from './AdminDataGridHooks';
import { GridPagination } from '../GridPagination/GridPagination';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { on } from 'events';

type CustomToolbarProps<T> = GridSlotsComponentsProps & {

    updateColumns: (columns: GridColDef[]) => void;
    columns: GridColDef[];
    headerName: string;
    rowCount?: number;
    page: number;
    testId?: string;
    pageSize: number;
    children: ReactElement;
    haveTreeView?: boolean;
    showAsTreeView?: boolean;
    showPagination?: boolean;
    searchTerm: string;
    loadEksport(eksportSettings: EksportSettings<T>): Promise<File>;
    setTreeView?(val: boolean): void;
    showFilter?: boolean;
    currentFilter?: { parameters: Filter };
    onChangeSelectedFilterGroups?: (filter: FilterGroup[]) => void;
    onChangeSelectedFilter: (filter: { parameters: Filter }) => void;
    defaultFilters: FilterGroup[];
    refresh: () => void;
    resetColumnSettings: () => void;
    ImportComponent: () => JSX.Element;
    exportComponentProps?: ExportComponentProps;
    canCreate: boolean;
    onCreate?: () => void;
};

export function CustomToolbar<T>({
    headerName,
    rowCount,
    refresh,
    resetColumnSettings,
    haveTreeView = false,
    showAsTreeView = false,
    showPagination = false,
    setTreeView,
    loadEksport,
    currentFilter,
    children,
    onChangeSelectedFilter,
    onChangeSelectedFilterGroups,
    exportComponentProps,
    defaultFilters,
    testId,
    showFilter = false,
    updateColumns,
    columns,
    searchTerm,
    page,
    pageSize,
    ImportComponent,
    canCreate,
    onCreate,
    ...props
}: CustomToolbarProps<T>) {
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] =
        useState<HTMLButtonElement>();
    const classes = useStyles();
    const { t } = useTranslation();
    const { navCreate } = useDataGridRoutes();

    async function handleDownloadEksportFile() {
        return await loadEksport({
            columns: columns.map((i) => {
                return {
                    columnId: i.field,
                    width: Math.floor(i.width ?? 0),
                    visible: !i.hide,
                    name: i.headerName,
                } as Column;
            }),
            totaltAntallISoket: pageSize,
            query: searchTerm,
            filterQueries: currentFilter,
            skip: page * (pageSize + 1),
        });
    }

    return (
        <>
            <GridToolbarContainer className={classes.toolbar} {...props}>
                <Typography
                    variant="subtitle1"
                    component="h2"
                    className={classes.headerText}
                >
                    {headerName} {rowCount ? '(' + rowCount + ')' : ''}
                </Typography>
                <Tooltip title={'Legg til ny'}>
                    <IconButton
                        disabled={!canCreate}
                        title={undefined}
                        test-id={testId + '-ny-btn'}
                        onClick={onCreate ?? navCreate}
                        className={classes.iconButton}
                        component={'span'}
                    >
                        <AddCircleOutlineOutlinedIcon
                            sx={{ color: canCreate ? 'secondary.main' : 'secondary.light' }}
                        />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Frisk opp">
                    <IconButton
                        onClick={refresh}
                        test-id={testId + '-friskOpp-btn'}
                        className={classes.iconButton}
                        aria-label={t('Frisk opp')}
                    >
                        <Refresh color="secondary" />
                    </IconButton>
                </Tooltip>
                {children}

                {(showAsTreeView && !showPagination) || <GridPagination className={classes.pagination} />}
                {haveTreeView && (
                    <Tooltip
                        title={!showAsTreeView ? t('Vis i tremodus') : t('Vis i listevisning')}
                    >
                        <IconButton
                            className={classes.iconButton}
                            test-id={
                                testId +
                                (!showAsTreeView
                                    ? '-visItremodus-btn'
                                    : '-visIlistevisning-btn')
                            }
                            onClick={() => {
                                setTreeView!(!showAsTreeView);
                            }}
                            size="small"
                        >
                            {!showAsTreeView ? (
                                <AcosIcon color={'white'}>file-tree</AcosIcon>
                            ) : (
                                <AcosIcon color={'white'}>table_chartpx</AcosIcon>
                            )}
                        </IconButton>
                    </Tooltip>
                )}

                <ExportComponent
                    className={exportComponentProps?.className ?? classes.iconButton}
                    handlers={[
                        {
                            label: 'Eksporter liste til csv fil',
                            handleDownload: handleDownloadEksportFile,
                        },
                        ...(exportComponentProps?.handlers ?? []),
                    ]}
                />

                <ImportComponent />
                {((defaultFilters && defaultFilters.length !== 0 && !showAsTreeView) ||
                    showFilter) && (
                        <Tooltip title={'Filter'}>
                            <IconButton
                                className={classes.iconButton}
                                onClick={(evt) => {
                                    setColumnsMenuAnchorEl(evt.currentTarget);
                                    setFilterMenuOpen(true);
                                }}
                                test-id={testId + '-filter-btn'}
                                aria-label={t('Filter') ?? ''}
                            >
                                <MaterialUIIcons.FilterAltIcon color="secondary" />
                            </IconButton>
                        </Tooltip>
                    )}

                <Tooltip title={'Kolonner'}>
                    <IconButton
                        onClick={(evt) => {
                            setColumnsMenuAnchorEl(evt.currentTarget);
                            setColumnsMenuOpen(true);
                        }}
                        test-id={testId + '-kolonner-btn'}
                        className={classes.iconButton}
                        aria-label={t('Kolonner')}
                    >
                        <ViewWeek color="secondary" />
                    </IconButton>
                </Tooltip>
            </GridToolbarContainer>
            <Divider light={true} className={classes.divider} />
            <AdminDataGridColumnsMenu
                updateColumns={updateColumns}
                testId={testId}
                columns={columns}
                isTreeView={showAsTreeView}
                open={columnsMenuOpen}
                anchorEl={columnsMenuAnchorEl}
                onClose={() => setColumnsMenuOpen(false)}
                reset={resetColumnSettings}
            />
            <AdminDataGridFilter
                onChangeSelectedFilter={onChangeSelectedFilter}
                onChangeSelectedFilterGroups={onChangeSelectedFilterGroups}
                defaultFilters={defaultFilters}
                anchorEl={columnsMenuAnchorEl}
                open={filterMenuOpen}
                onClose={() => setFilterMenuOpen(false)}
            />
        </>
    );
}

export function CustomErrorOverlay<T>({
    message,
    title
}: // ...p
    CustomToolbarProps<T> & { message?: string, title?: string }) {
    const { t } = useTranslation();
    return (
        <Box sx={{ p: 3 }}>
            {title ?? t('Feil')}
            <span>{message ?? t('En feil har oppstått')}</span>
        </Box>
    );
}

export const GridNoRowsCustomOverlay = React.forwardRef<HTMLDivElement,
    GridOverlayProps & { searchMode: boolean; hideNoRows: boolean }>(function GridNoRowsOverlay(props, ref) {
        const apiRef = useGridApiContext();
        const noRowsLabel = apiRef.current.getLocaleText('noRowsLabel');
        const noResultsLabel = apiRef.current.getLocaleText('noResultsOverlayLabel');
        const { searchMode, hideNoRows = false, ...other } = props;
        return hideNoRows ? (
            <></>
        ) : (
            <GridOverlay ref={ref} {...other}>
                {searchMode ? noResultsLabel : noRowsLabel}
            </GridOverlay>
        );
    });

type ExportHandler = {
    label: string;
    handleDownload?: () => Promise<File | null>;
    handleAction?: () => void;
};

export interface ExportComponentProps {
    handlers: ExportHandler[];
    className?: string;
}

export function ExportComponent(props: ExportComponentProps) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [infoParam, setInfoParam] = useState<{
        open: boolean;
        success?: boolean;
        msg?: string;
    }>({ open: false });

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
    const [coordinatesOfMenu, setCoordinatesOfMenu] = useState<{
        X?: number;
        Y?: number;
    }>({});

    const [confirmDialogOpen, setConfirmDialogOpenProps] = useState({
        open: false,
        title: '',
        message: '',
        handler: null as ExportHandler | null,

    });  // State for ConfirmDialog

    const { t } = useTranslation();
    function handleCloseConfirmationDialogWindow() {
        setConfirmDialogOpenProps({ open: false, message: '', title: '', handler: null });
    }
    function handleCloseFortesetteTilEksportDialog() {
        handleCloseConfirmationDialogWindow();
    }
    function handleFortsettOgEksporter() {
        if (confirmDialogOpen.handler) {
            if (confirmDialogOpen.handler.handleDownload)
                handleDownload(confirmDialogOpen.handler.handleDownload);
            if (confirmDialogOpen.handler.handleAction)
                confirmDialogOpen.handler.handleAction();
        }
        handleCloseConfirmationDialogWindow();
    }
    function handleClick(evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        if (props.handlers.length > 1) {
            setAnchorEl(evt.currentTarget);
            setCoordinatesOfMenu({ X: undefined, Y: undefined });
            setOpen(true);
        }
        if (props.handlers.length === 1) handleExport(props.handlers[0]);
    }

    async function handleExport(handler: ExportHandler) {
        setConfirmDialogOpenProps({
            open: true,
            title: handler.label,
            message: 'Dersom du har mange verdier å eksportere, kan dette ta litt tid. Vil du fortsette?',
            handler: handler as any,
        });
    }

    function handleMenuClose() {
        setOpen(false);
    }

    async function handleDownload(loader: () => Promise<File | null>) {
        setLoading(true);
        try {
            const file = await loader();
            if (file != null) {
                downloadFile(file);
            }
        } catch (err: any) {
            setInfoParam({ success: false, open: true, msg: err.message });
        }
        setLoading(false);
    }

    const title =
        props.handlers.length === 1 ? props.handlers[0].label : t('Eksporter');

    return (
        <>
            <FullscreenSpinner loading={loading} />
            <Tooltip title={title}>
                <IconButton
                    onClick={handleClick}
                    className={props.className}
                    color="secondary"
                    aria-label={title}
                >
                    <MaterialUIIcons.FileDownload color="secondary" fontSize="small" />
                </IconButton>
            </Tooltip>
            {infoParam.open && (<MessageDialog
                title={infoParam.success ? 'Rapport' : 'Feil'}
                open={infoParam.open}
                onClose={() => setInfoParam({ open: false })}
            >
                <div>
                    <p style={{ whiteSpace: 'pre-line', display: 'block', width: 'max-content' }}>
                        {infoParam.msg}
                    </p>
                </div>
            </MessageDialog>)}
            <EditPanelContextMenu
                anchorEl={anchorEl}
                open={open}
                X={coordinatesOfMenu.X}
                Y={coordinatesOfMenu.Y}
                onClose={handleMenuClose}
            >
                <div>
                    {props.handlers.map((item, index) => {
                        return (
                            <MenuItem key={index} onClick={() =>
                                handleExport(item)}
                            >
                                <ListItemText primary={item.label} />
                            </MenuItem>

                        );
                    })}
                </div>
            </EditPanelContextMenu>
            <ConfirmDialog
                open={confirmDialogOpen.open}
                title="Eksporter"

                onConfirm={() => {
                    handleFortsettOgEksporter();
                }}
                onClose={handleCloseFortesetteTilEksportDialog}
                onAbort={handleCloseFortesetteTilEksportDialog}
            >
                {t(`${confirmDialogOpen.message}`)}
            </ConfirmDialog>


        </>
    );
}

